
import React, { useState, useEffect } from 'react';
import CountryFlag from '../../shared/components/CountryFlag';
import {
    Empty, Card, Form, Input, Button, Tabs, message, Avatar,
    Upload, Typography, Space, Divider, Switch, Alert, List, Select
} from 'antd';
import {
    UserOutlined, MailOutlined, LockOutlined,
    SaveOutlined, UploadOutlined, BellOutlined,
    SecurityScanOutlined, ApiOutlined
} from '@ant-design/icons';
import { useAuth } from '../../shared/hooks/useAuth';
// 本页是管理员「个人资料」（自己的资料/密码/偏好/API 密钥），走 /api/user/* 自助端点；
// admin userService 是用户管理用的，没有 getApiKeys 等方法（曾致 "getApiKeys is not a function"）
import userService from '../../user/services/userService';
// 原「系统设置」tab 已移除：它编辑的 7 个键在 system_settings 表里不存在（保存是空操作）。全局设置见 /system/settings。
import phoneCountryCodes from '../../shared/utils/phoneCountryCodes';

const { Title, Text } = Typography;
const { TabPane } = Tabs;

const Settings = () => {
    const { currentUser, updateUser } = useAuth();
    const [profileForm] = Form.useForm();
    const [passwordForm] = Form.useForm();
    const [notificationsForm] = Form.useForm();
    const [apiKeysForm] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [avatarUrl, setAvatarUrl] = useState('');
    const [apiKeys, setApiKeys] = useState([]);

    useEffect(() => {
        if (currentUser) {
            profileForm.setFieldsValue({
                username: currentUser.username,
                email: currentUser.email,
                phone: currentUser.phone,
                phone_country_code: currentUser.phone_country_code || '+86',
                company: currentUser.company,
            });

            notificationsForm.setFieldsValue({
                email_notifications: currentUser.preferences?.email_notifications ?? true,
                security_alerts: currentUser.preferences?.security_alerts ?? true,
                marketing_emails: currentUser.preferences?.marketing_emails ?? false,
            });

            setAvatarUrl(currentUser.avatar_url);

            // 获取API密钥
            fetchApiKeys();

        }
    }, [currentUser]);

    const fetchApiKeys = async () => {
        try {
            setLoading(true);
            const response = await userService.getApiKeys();
            if (response.success) {
                setApiKeys(response.data);
            }
        } catch (error) {
            console.error('Error fetching API keys:', error);
            message.error('无法获取API密钥');
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateProfile = async (values) => {
        try {
            setLoading(true);
            const response = await userService.updateProfile(values);
            if (response.success) {
                message.success('个人资料已更新');
                updateUser(response.data);
            } else {
                message.error(response.error || '更新个人资料失败');
            }
        } catch (error) {
            message.error('更新个人资料失败: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleUpdatePassword = async (values) => {
        try {
            setLoading(true);
            const response = await userService.changePassword(values.current_password, values.new_password);
            if (response.success) {
                message.success('密码已更新');
                passwordForm.resetFields();
            } else {
                message.error(response.error || '更新密码失败');
            }
        } catch (error) {
            message.error('更新密码失败: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateNotifications = async (values) => {
        try {
            setLoading(true);
            const response = await userService.updatePreferences(values);
            if (response.success) {
                message.success('通知设置已更新');
                updateUser({
                    ...currentUser,
                    preferences: values
                });
            } else {
                message.error(response.error || '更新通知设置失败');
            }
        } catch (error) {
            message.error('更新通知设置失败: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleAvatarChange = async (info) => {
        if (info.file.status === 'uploading') {
            setLoading(true);
            return;
        }

        if (info.file.status === 'done') {
            setLoading(false);
            const response = info.file.response;
            if (response.success) {
                setAvatarUrl(response.data.url);
                message.success('头像已更新');
                updateUser({
                    ...currentUser,
                    avatar_url: response.data.url
                });
            } else {
                message.error(response.error || '上传头像失败');
            }
        } else if (info.file.status === 'error') {
            setLoading(false);
            message.error('上传头像失败');
        }
    };

    const generateApiKey = async () => {
        try {
            setLoading(true);
            const description = apiKeysForm.getFieldValue('description');

            if (!description) {
                message.warning('请输入API密钥描述');
                return;
            }

            const response = await userService.generateApiKey(description);
            if (response.success) {
                message.success('API密钥已生成');
                apiKeysForm.resetFields();

                // 显示密钥
                message.info({
                    content: (
                        <div>
                            <p>请保存您的API密钥，它不会再次显示：</p>
                            <Text code copyable>{response.data.api_key}</Text>
                        </div>
                    ),
                    duration: 0,
                });

                // 刷新密钥列表
                fetchApiKeys();
            } else {
                message.error(response.error || '生成API密钥失败');
            }
        } catch (error) {
            message.error('生成API密钥失败: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const deleteApiKey = async (keyId) => {
        try {
            setLoading(true);
            const response = await userService.deleteApiKey(keyId);
            if (response.success) {
                message.success('API密钥已删除');
                fetchApiKeys();
            } else {
                message.error(response.error || '删除API密钥失败');
            }
        } catch (error) {
            message.error('删除API密钥失败: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div>
            <Title level={2}>个人资料</Title>

            <Tabs defaultActiveKey="profile">
                <TabPane
                    tab={<span><UserOutlined />个人资料</span>}
                    key="profile"
                >
                    <Card>
                        <div style={{ display: 'flex', marginBottom: 24 }}>
                            <div style={{ marginRight: 24 }}>
                                <Avatar
                                    size={96}
                                    icon={<UserOutlined />}
                                    src={avatarUrl}
                                />
                                <div style={{ marginTop: 16 }}>
                                    <Upload
                                        name="avatar"
                                        action="/api/user/avatar"
                                        showUploadList={false}
                                        onChange={handleAvatarChange}
                                    >
                                        <Button icon={<UploadOutlined />}>更换头像</Button>
                                    </Upload>
                                </div>
                            </div>

                            <div style={{ flex: 1 }}>
                                <Form
                                    form={profileForm}
                                    layout="vertical"
                                    onFinish={handleUpdateProfile}
                                    className="form-container"
                                >
                                    <Form.Item
                                        name="username"
                                        label="用户名"
                                        rules={[{ required: true, message: '请输入用户名' }]}
                                    >
                                        <Input prefix={<UserOutlined />} disabled />
                                    </Form.Item>

                                    <Form.Item
                                        name="email"
                                        label="电子邮箱"
                                        rules={[
                                            { required: true, message: '请输入电子邮箱' },
                                            { type: 'email', message: '请输入有效的电子邮箱' }
                                        ]}
                                    >
                                        <Input prefix={<MailOutlined />} />
                                    </Form.Item>

                                    <Form.Item label="手机号码">
                                        <Input.Group compact>
                                            <Form.Item name="phone_country_code" noStyle>
                                                <Select
                                                    style={{ width: 140 }}
                                                    showSearch
                                                    filterOption={(input, option) =>
                                                        String(option.search || '').toLowerCase().includes(input.toLowerCase())
                                                    }
                                                >
                                                    {phoneCountryCodes.map(item => (
                                                        <Select.Option key={item.code} value={item.code} search={`${item.code} ${item.short} ${item.label}`}>
                                                            <CountryFlag code={item.short} />{item.code} {item.label}
                                                        </Select.Option>
                                                    ))}
                                                </Select>
                                            </Form.Item>
                                            <Form.Item name="phone" noStyle>
                                                <Input style={{ width: 'calc(100% - 140px)' }} placeholder="手机号码" />
                                            </Form.Item>
                                        </Input.Group>
                                    </Form.Item>

                                    <Form.Item
                                        name="company"
                                        label="公司名称"
                                    >
                                        <Input />
                                    </Form.Item>

                                    <Form.Item>
                                        <Button
                                            type="primary"
                                            htmlType="submit"
                                            loading={loading}
                                            icon={<SaveOutlined />}
                                        >
                                            保存修改
                                        </Button>
                                    </Form.Item>
                                </Form>
                            </div>
                        </div>
                    </Card>
                </TabPane>

                <TabPane
                    tab={<span><LockOutlined />安全设置</span>}
                    key="security"
                >
                    <Card>
                        <Title level={4}>修改密码</Title>
                        <Form
                            form={passwordForm}
                            layout="vertical"
                            onFinish={handleUpdatePassword}
                            className="form-container"
                        >
                            <Form.Item
                                name="current_password"
                                label="当前密码"
                                rules={[{ required: true, message: '请输入当前密码' }]}
                            >
                                <Input.Password prefix={<LockOutlined />} />
                            </Form.Item>

                            <Form.Item
                                name="new_password"
                                label="新密码"
                                rules={[
                                    { required: true, message: '请输入新密码' },
                                    { min: 8, message: '密码至少8个字符' },
                                    { pattern: /^(?=.*[A-Za-z])(?=.*\d)/, message: '需同时包含字母和数字' }
                                ]}
                                hasFeedback
                            >
                                <Input.Password prefix={<LockOutlined />} />
                            </Form.Item>

                            <Form.Item
                                name="confirm_password"
                                label="确认新密码"
                                dependencies={['new_password']}
                                hasFeedback
                                rules={[
                                    { required: true, message: '请确认新密码' },
                                    ({ getFieldValue }) => ({
                                        validator(_, value) {
                                            if (!value || getFieldValue('new_password') === value) {
                                                return Promise.resolve();
                                            }
                                            return Promise.reject(new Error('两次输入的密码不一致'));
                                        },
                                    }),
                                ]}
                            >
                                <Input.Password prefix={<LockOutlined />} />
                            </Form.Item>

                            <Form.Item>
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    loading={loading}
                                    icon={<SaveOutlined />}
                                >
                                    更新密码
                                </Button>
                            </Form.Item>
                        </Form>

                        <Divider />

                        <Title level={4}>账户安全</Title>
                        <Alert
                            message="两步验证"
                            description="两步验证可以提高您账户的安全性。登录时除了密码外，还需要输入一次性验证码。"
                            type="info"
                            showIcon
                            action={
                                <Button type="primary" icon={<SecurityScanOutlined />}>
                                    设置两步验证
                                </Button>
                            }
                            style={{ marginBottom: 16 }}
                        />
                    </Card>
                </TabPane>

                <TabPane
                    tab={<span><BellOutlined />通知设置</span>}
                    key="notifications"
                >
                    <Card>
                        <Form
                            form={notificationsForm}
                            layout="vertical"
                            onFinish={handleUpdateNotifications}
                            className="form-container"
                        >
                            <Form.Item
                                name="email_notifications"
                                valuePropName="checked"
                            >
                                <Switch checkedChildren="开" unCheckedChildren="关" /> 电子邮件通知
                            </Form.Item>

                            <Form.Item
                                name="security_alerts"
                                valuePropName="checked"
                            >
                                <Switch checkedChildren="开" unCheckedChildren="关" /> 安全警报
                            </Form.Item>

                            <Form.Item
                                name="marketing_emails"
                                valuePropName="checked"
                            >
                                <Switch checkedChildren="开" unCheckedChildren="关" /> 营销邮件
                            </Form.Item>

                            <Form.Item>
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    loading={loading}
                                    icon={<SaveOutlined />}
                                >
                                    保存设置
                                </Button>
                            </Form.Item>
                        </Form>
                    </Card>
                </TabPane>

                <TabPane
                    tab={<span><ApiOutlined />API密钥</span>}
                    key="api-keys"
                >
                    <Card>
                        <Alert
                            message="API密钥用途"
                            description="API密钥用于程序化访问我们的API。请确保安全保存密钥，并在不需要时删除密钥。"
                            type="info"
                            showIcon
                            style={{ marginBottom: 16 }}
                        />

                        <Title level={4}>生成新密钥</Title>
                        <Form
                            form={apiKeysForm}
                            layout="inline"
                            style={{ marginBottom: 16 }}
                        >
                            <Form.Item
                                name="description"
                                rules={[{ required: true, message: '请输入密钥描述' }]}
                                style={{ flex: 1 }}
                            >
                                <Input placeholder="API密钥描述" />
                            </Form.Item>

                            <Form.Item>
                                <Button
                                    type="primary"
                                    onClick={generateApiKey}
                                    loading={loading}
                                    icon={<ApiOutlined />}
                                >
                                    生成密钥
                                </Button>
                            </Form.Item>
                        </Form>

                        <Divider />

                        <Title level={4}>现有密钥</Title>
                        {apiKeys.length === 0 ? (
                            <Empty description="暂无API密钥" />
                        ) : (
                            <List
                                dataSource={apiKeys}
                                renderItem={key => (
                                    <List.Item
                                        actions={[
                                            <Button
                                                type="text"
                                                danger
                                                onClick={() => deleteApiKey(key.id)}
                                            >
                                                删除
                                            </Button>
                                        ]}
                                    >
                                        <List.Item.Meta
                                            title={key.description}
                                            description={
                                                <Space direction="vertical" size={0}>
                                                    <Text type="secondary">密钥ID: {key.key_id}</Text>
                                                    <Text type="secondary">创建时间: {new Date(key.created_at).toLocaleString()}</Text>
                                                    <Text type="secondary">最后使用: {key.last_used_at ? new Date(key.last_used_at).toLocaleString() : '从未使用'}</Text>
                                                </Space>
                                            }
                                        />
                                    </List.Item>
                                )}
                            />
                        )}
                    </Card>
                </TabPane>

            </Tabs>
        </div>
    );
};

export default Settings;
